import { DynamicFormMessage, NavigationMessage } from "@/types/agent/message";
import type { StreamEventHandlerDeps, ToolCallEvent } from "./types";
import { getCurrentPage } from "@/lib/utils/getCurrentPage";
import { trackAgentResponse } from "../analytics";

const COLLECTION_TO_PREFIX: Record<string, string> = {
  page: "",
  pages: "",
  case: "cases",
  cases: "cases",
  industry: "branchen",
  industries: "branchen",
  news: "news",
  service: "leistungen",
  services: "leistungen",
  job: "karriere",
  jobs: "karriere",
  careerStory: "career-stories",
  careerStories: "career-stories",
  career_story: "career-stories",
  career_stories: "career-stories",
  person: "kontakt",
};

const resolveUrlFromCollection = (
  slug: string,
  collection: string,
  section?: string
): string | null => {
  const prefix = COLLECTION_TO_PREFIX[collection];
  if (prefix === undefined) return null;

  const normalizedSlug = slug === "main" ? "" : slug;
  const path = prefix ? `/${prefix}/${normalizedSlug}` : `/${normalizedSlug}`;
  const anchor = section ? `#${normalizeAnchor(section)}` : "";
  return `${path}${anchor}`;
};

const hasFileInFormData = (
  data: Record<string, string | File | File[]>
): boolean =>
  Object.values(data).some(
    (value) =>
      value instanceof File ||
      (Array.isArray(value) && value.length > 0 && value[0] instanceof File)
  );

const submitFormToAgent = (
  formId: string,
  data: Record<string, string | File | File[]>,
  deps: Pick<StreamEventHandlerDeps, "sendUserMessageRef">
): void => {
  if (hasFileInFormData(data)) {
    deps.sendUserMessageRef.current?.(`[Form Submitted: ${formId}]`, true, {
      formSubmission: {
        formId,
        data,
      },
    });
    return;
  }

  const textData: Record<string, string> = {};
  Object.entries(data).forEach(([key, value]) => {
    if (typeof value === "string") {
      textData[key] = value;
    }
  });
  deps.sendUserMessageRef.current?.(
    `[Form Submitted: ${formId}] \n ${JSON.stringify(textData)}`,
    true
  );
};

const normalizeAnchor = (value: string): string =>
  value.startsWith("#") ? value.slice(1) : value;

const buildUrlWithAnchor = (page: string, section: string): string => {
  const normalizedSection = normalizeAnchor(section);
  if (!normalizedSection) {
    return page;
  }

  const [baseUrl] = page.split("#");
  return `${baseUrl}#${normalizedSection}`;
};

const getCurrentPath = (): string => {
  if (typeof window === "undefined") {
    return "/";
  }

  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

const getComparablePage = (value: string): string => {
  const [withoutHash] = value.split("#");
  const [withoutQuery] = withoutHash.split("?");
  const segments = withoutQuery.split("/").filter(Boolean);
  const page = segments.length > 0 ? segments[segments.length - 1] : "";

  return page === "main" ? "" : page;
};

/**
 * Page names in the navigation bubble sit on a single line next to an icon, and a
 * CMS title can be a whole sentence ("Über MUUUH! | Consulting, Tech & …").
 */
const MAX_PAGE_NAME_LENGTH = 10;

const truncatePageName = (name: string): string =>
  name.length > MAX_PAGE_NAME_LENGTH
    ? `${name.slice(0, MAX_PAGE_NAME_LENGTH).trimEnd()}…`
    : name;

/**
 * The CMS title of a page, so the bubble can name it the way the site does.
 * `null` for anything Directus doesn't title (Personio jobs, unknown paths) —
 * the caller keeps the slug-derived fallback.
 */
const fetchPageTitle = async (path: string): Promise<string | null> => {
  // The agent may hand us an absolute URL; nothing on another host is in our CMS.
  if (/^https?:\/\//.test(path)) return null;
  try {
    const res = await fetch(`/api/page-title?path=${encodeURIComponent(path)}`);
    if (!res.ok) return null;
    const { title } = (await res.json()) as { title: string | null };
    return title;
  } catch {
    return null;
  }
};

const handleNavigationToolCall = (
  event: Extract<ToolCallEvent, { name: "navigate" | "navigate_and_scroll" }>,
  deps: Pick<StreamEventHandlerDeps, "messageHistory" | "scrollToAnchor" | "t">
): string => {
  const currentPage = getCurrentPage();
  const currentPath = getCurrentPath();
  const targetPage = event.args.page;
  const section = event.args.section;
  let navigationUrl = targetPage;

  if (event.args.collection) {
    navigationUrl =
      resolveUrlFromCollection(targetPage, event.args.collection, section) ??
      targetPage;
  } else if (section) {
    navigationUrl = buildUrlWithAnchor(targetPage, section);
  }

  if (getComparablePage(currentPage) === getComparablePage(targetPage)) {
    return "already on target page";
  }

  const checkPageName = (page: string) => {
    if (page === "") {
      return deps.t.navigation.startPage;
    }

    return truncatePageName(
      page
        .split("-")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join("-")
    );
  };

  const message = deps.messageHistory.addNavigationMessage({
    text: deps.t.navigation.goToPage.replace(
      "{page}",
      checkPageName(targetPage)
    ),
    backText: deps.t.navigation.backToPage.replace(
      "{page}",
      checkPageName(currentPage)
    ),
    backUrl: currentPath,
    url: navigationUrl,
  });
  trackAgentResponse("navigate");

  // A slug is a URL, not a page name ("ai-agenten" only reads like a label by
  // accident). Swap in the CMS titles once they arrive: the bubble is already on
  // screen and counting down, so this never delays the navigation. Skipped where
  // the fallback is the localized "Start" rather than a slug.
  void Promise.all([
    getComparablePage(targetPage) ? fetchPageTitle(navigationUrl) : null,
    getComparablePage(currentPage) ? fetchPageTitle(currentPath) : null,
  ]).then(([targetTitle, currentTitle]) => {
    if (!targetTitle && !currentTitle) return;
    message.update?.({
      ...(targetTitle && {
        text: deps.t.navigation.goToPage.replace(
          "{page}",
          truncatePageName(targetTitle)
        ),
      }),
      ...(currentTitle && {
        backText: deps.t.navigation.backToPage.replace(
          "{page}",
          truncatePageName(currentTitle)
        ),
      }),
    } as Partial<NavigationMessage>);
  });

  return "navigation shown";
};

const handleScrollToolCall = (
  event: Extract<ToolCallEvent, { name: "scroll" }>,
  deps: Pick<StreamEventHandlerDeps, "scrollToAnchor">
): void => {
  deps.scrollToAnchor(event.args.section);
};

const handleDynamicFormToolCall = (
  event: Extract<ToolCallEvent, { name: "show_dynamic_form" }>,
  deps: Pick<StreamEventHandlerDeps, "messageHistory" | "sendUserMessageRef">
): void => {
  deps.messageHistory.addDynamicFormMessage({
    formId: event.args.form_id as DynamicFormMessage["formId"],
    title: event.args.title,
    description: event.args.description,
    fields: event.args.fields,
    submitText: event.args.submit_text,
    onSubmit: (formId, data) => {
      submitFormToAgent(formId, data, deps);
    },
  });
};

const handleActionButtonsToolCall = (
  event: Extract<ToolCallEvent, { name: "show_action_buttons" }>,
  deps: Pick<
    StreamEventHandlerDeps,
    "messageHistory" | "sendUserMessageRef" | "t"
  >
): void => {
  deps.messageHistory.addSelectMessage({
    title: deps.t.selectOption,
    options: event.args.buttons.map((btn) => ({
      text: btn.text,
      value: btn.action,
    })),
    onSelect: (value) => {
      deps.sendUserMessageRef.current?.(value, true);
    },
  });
};

const handleAppointmentDatesToolCall = (
  event: Extract<ToolCallEvent, { name: "suggest_appointment_dates" }>,
  deps: Pick<
    StreamEventHandlerDeps,
    "messageHistory" | "sendUserMessageRef" | "t"
  >
): void => {
  deps.messageHistory.addSelectMessage({
    title: deps.t.selectAppointment,
    options: event.args.dates.map((date) => ({
      text: date,
      value: date,
    })),
    onSelect: (value) => {
      deps.sendUserMessageRef.current?.(`[Termin ausgewählt: ${value}]`, true);
    },
  });
};

const handleLeadContactFormToolCall = (
  event: Extract<ToolCallEvent, { name: "show_lead_contact_form" }>,
  deps: Pick<
    StreamEventHandlerDeps,
    "messageHistory" | "sendUserMessageRef" | "t"
  >
): void => {
  const f = deps.t.leadContactForm;
  deps.messageHistory.addDynamicFormMessage({
    formId: "lead_contact_form",
    title: f.title,
    description: f.description,
    fields: [
      {
        name: "name",
        label: f.fields.name,
        type: "text",
        required: true,
        value: event.args.name,
      },
      {
        name: "email",
        label: f.fields.email,
        type: "text",
        required: true,
        value: event.args.email,
      },
      {
        name: "company",
        label: f.fields.company,
        type: "text",
        required: false,
        value: event.args.company,
      },
      {
        name: "phone",
        label: f.fields.phone,
        type: "text",
        required: false,
        value: event.args.phone,
      },
      {
        name: "details",
        label: f.fields.details,
        type: "textarea",
        required: false,
        value: event.args.details,
      },
    ],
    submitText: f.submit,
    onSubmit: (formId, data) => {
      submitFormToAgent(formId, data, deps);
    },
  });
};

const handleApplicationFormToolCall = (
  event: Extract<ToolCallEvent, { name: "show_application_form" }>,
  deps: Pick<
    StreamEventHandlerDeps,
    "messageHistory" | "sendUserMessageRef" | "t"
  >
): void => {
  const f = deps.t.applicationForm;
  deps.messageHistory.addDynamicFormMessage({
    formId: "application_form",
    title: event.args.job_title
      ? f.titleWithJob.replace("{jobTitle}", event.args.job_title)
      : f.title,
    description: f.description,
    fields: [
      {
        name: "vorname",
        label: f.fields.firstName,
        type: "text",
        required: true,
        value: event.args.vorname,
      },
      {
        name: "nachname",
        label: f.fields.lastName,
        type: "text",
        required: true,
        value: event.args.nachname,
      },
      {
        name: "email",
        label: f.fields.email,
        type: "text",
        required: true,
        value: event.args.email,
      },
      {
        name: "telefonnummer",
        label: f.fields.phone,
        type: "text",
        required: true,
        value: event.args.telefonnummer,
      },
    ],
    submitText: f.submit,
    onSubmit: (formId, data) => {
      submitFormToAgent(formId, data, deps);
    },
  });
};

const handleDocumentUploadFormToolCall = (
  deps: Pick<
    StreamEventHandlerDeps,
    "messageHistory" | "sendUserMessageRef" | "t"
  >
): void => {
  const f = deps.t.documentUploadForm;
  deps.messageHistory.addDynamicFormMessage({
    formId: "document_upload_form",
    title: f.title,
    description: f.description,
    fields: [
      { name: "dokument", label: f.documentLabel, type: "pdf", required: true },
    ],
    submitText: f.submit,
    onSubmit: (formId, data) => {
      submitFormToAgent(formId, data, deps);
    },
  });
};

export const handleToolCallEvent = (
  event: ToolCallEvent,
  deps: Pick<
    StreamEventHandlerDeps,
    "messageHistory" | "scrollToAnchor" | "sendUserMessageRef" | "t"
  >
): void | string => {
  switch (event.name) {
    case "navigate":
    case "navigate_and_scroll":
      return handleNavigationToolCall(event, deps);
    case "scroll":
      handleScrollToolCall(event, deps);
      break;
    case "show_dynamic_form":
      handleDynamicFormToolCall(event, deps);
      break;
    case "show_action_buttons":
      handleActionButtonsToolCall(event, deps);
      break;
    case "suggest_appointment_dates":
      handleAppointmentDatesToolCall(event, deps);
      break;
    case "show_lead_contact_form":
      handleLeadContactFormToolCall(event, deps);
      break;
    case "show_application_form":
      handleApplicationFormToolCall(event, deps);
      break;
    case "show_document_upload_form":
      handleDocumentUploadFormToolCall(deps);
      break;
  }
};
